import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { analyticsEnv } from '../config/analyticsEnv';
import checkoutAnalyticsService from '../services/checkoutAnalyticsService';

type ConsentPreference = 'granted' | 'denied';

export type ConsentStatus = ConsentPreference | 'unknown';

export interface AnalyticsConsentContextValue {
  consentStatus: ConsentStatus;
  hasConsent: boolean;
  isConsentRequired: boolean;
  canManageConsent: boolean;
  isHydrated: boolean;
  isConsentPromptOpen: boolean;
  setConsent: (granted: boolean) => void;
  resetConsent: () => void;
  openConsentPrompt: () => void;
  closeConsentPrompt: () => void;
  shouldEnableAnalytics: boolean;
}

const AnalyticsConsentContext = createContext<AnalyticsConsentContextValue | null>(null);

const initialPreference: ConsentPreference = analyticsEnv.defaultConsent ? 'granted' : 'denied';

const readStoredPreference = (key: string): ConsentPreference | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const stored = window.localStorage.getItem(key);
    if (stored === 'granted' || stored === 'denied') {
      return stored;
    }
    return null;
  } catch {
    return null;
  }
};

const persistPreference = (key: string, preference: ConsentPreference): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(key, preference);
  } catch {
    // Ignore storage write errors (e.g. private browsing)
  }
};

const removeStoredPreference = (key: string): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.removeItem(key);
  } catch {
    // Ignore storage removal errors
  }
};

export const AnalyticsConsentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [consentStatus, setConsentStatus] = useState<ConsentStatus>(() =>
    analyticsEnv.requireConsent ? 'unknown' : initialPreference
  );
  const [isHydrated, setHydrated] = useState<boolean>(() => !analyticsEnv.requireConsent);
  const [isConsentPromptOpen, setConsentPromptOpen] = useState<boolean>(() => analyticsEnv.requireConsent);

  useEffect(() => {
    checkoutAnalyticsService.updateConfig({
      googleAnalyticsId: analyticsEnv.measurementId,
      gaDebugMode: analyticsEnv.debugView,
      autoEnable: analyticsEnv.shouldAutoEnable
    });

    if (!analyticsEnv.shouldAutoEnable) {
      checkoutAnalyticsService.disableAnalytics();
    }
  }, []);

  useEffect(() => {
    if (!analyticsEnv.enabled) {
      checkoutAnalyticsService.disableAnalytics();
      return;
    }

    if (!analyticsEnv.requireConsent && analyticsEnv.shouldAutoEnable) {
      checkoutAnalyticsService.enableAnalytics();
      return;
    }

    if (!analyticsEnv.shouldAutoEnable) {
      checkoutAnalyticsService.disableAnalytics();
    }
  }, []);

  useEffect(() => {
    if (!analyticsEnv.requireConsent) {
      return;
    }

    const stored = readStoredPreference(analyticsEnv.consentStorageKey);
    if (stored) {
      setConsentStatus(stored);
      setConsentPromptOpen(false);
    } else {
      setConsentStatus('unknown');
      setConsentPromptOpen(true);
    }
    setHydrated(true);
  }, []);

  const effectiveStatus: ConsentPreference =
    consentStatus === 'unknown' ? initialPreference : consentStatus;

  const shouldEnableAnalytics =
    analyticsEnv.enabled &&
    !!analyticsEnv.measurementId &&
    (!analyticsEnv.requireConsent || effectiveStatus === 'granted');
  const canManageConsent = analyticsEnv.enabled && analyticsEnv.requireConsent;

  useEffect(() => {
    if (!analyticsEnv.enabled) {
      checkoutAnalyticsService.disableAnalytics();
      return;
    }

    if (shouldEnableAnalytics) {
      checkoutAnalyticsService.enableAnalytics();
    } else {
      checkoutAnalyticsService.disableAnalytics();
    }
  }, [shouldEnableAnalytics]);

  const setConsent = useCallback((granted: boolean) => {
    const preference: ConsentPreference = granted ? 'granted' : 'denied';
    setConsentStatus(preference);
    persistPreference(analyticsEnv.consentStorageKey, preference);
    setConsentPromptOpen(false);
  }, []);

  const resetConsent = useCallback(() => {
    removeStoredPreference(analyticsEnv.consentStorageKey);
    if (analyticsEnv.requireConsent) {
      setConsentStatus('unknown');
      setConsentPromptOpen(true);
      return;
    }

    setConsentStatus(initialPreference);
    setConsentPromptOpen(false);
  }, []);

  const openConsentPrompt = useCallback(() => {
    if (!analyticsEnv.requireConsent) {
      return;
    }

    setConsentPromptOpen(true);
  }, []);

  const closeConsentPrompt = useCallback(() => {
    if (consentStatus === 'unknown') {
      return;
    }

    setConsentPromptOpen(false);
  }, [consentStatus]);

  const contextValue = useMemo<AnalyticsConsentContextValue>(
    () => ({
      consentStatus,
      hasConsent: effectiveStatus === 'granted',
      isConsentRequired: analyticsEnv.requireConsent,
      canManageConsent,
      isHydrated,
      isConsentPromptOpen:
        canManageConsent && isHydrated && isConsentPromptOpen,
      setConsent,
      resetConsent,
      openConsentPrompt,
      closeConsentPrompt,
      shouldEnableAnalytics
    }),
    [
      closeConsentPrompt,
      canManageConsent,
      consentStatus,
      effectiveStatus,
      isConsentPromptOpen,
      isHydrated,
      openConsentPrompt,
      resetConsent,
      setConsent,
      shouldEnableAnalytics
    ]
  );

  return (
    <AnalyticsConsentContext.Provider value={contextValue}>
      {children}
    </AnalyticsConsentContext.Provider>
  );
};

export const useAnalyticsConsent = (): AnalyticsConsentContextValue => {
  const context = useContext(AnalyticsConsentContext);

  if (!context) {
    throw new Error('useAnalyticsConsent must be used within an AnalyticsConsentProvider');
  }

  return context;
};
